import { isValidCron } from 'cron-validator';
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  mimes,
  required,
  email,
  alpha,
  alpha_dash,
  alpha_spaces,
  alpha_num,
  double,
  length,
  confirmed,
  max,
  min,
  min_value,
  max_value,
  numeric,
  required_if,
  size,
  image
} from 'vee-validate/dist/rules';

import moment from 'moment';

extend('mimes', mimes);
extend('size', size);
extend('image', image);
extend('double', double);
extend('alpha', alpha);
extend('alpha_dash', alpha_dash);
extend('alpha_spaces', alpha_spaces);
extend('alpha_num', alpha_num);
extend('confirmed', confirmed);
extend('required', {
  ...required,
  message: 'This field is required'
});
extend('required_if', required_if);
extend('numeric', numeric);
extend('email', email);
extend('length', length);
extend('isMultiple1000', {
  validate: (value) => {
    return value % 1000 == 0;
  },
  message: 'Value must be in multiples of a thousand (1000). Eg: 1000, 2000'
});
extend('telephone', {
  validate: (value) => {
    const normalized = value.trim().startsWith('+') ? value.trim() : `+${value.trim()}`;
    const phoneNumber = parsePhoneNumberFromString(normalized);
    return phoneNumber ? phoneNumber.isValid() : false;
  },
  message: 'Invalid phone number. Include country code. E.g. 18761234567 or +18761234567'
});
extend('min_value', {
  ...min_value,
  message: '{_field_} may not be less than {min}'
});
extend('max_value', {
  ...max_value,
  message: '{_field_} may not be greater than {max}'
});
extend('min_value', {
  ...min_value,
  message: '{_field_} may not be less than {min}'
});
extend('max', {
  ...max,
  message: '{_field_} may not be greater than {length} characters'
});
extend('min', {
  ...min,
  message: '{_field_} must be {length} characters or more'
});
extend('max_date', {
  params: ['max'],
  validate(value, { max }) {
    if (!value || max == 'null') return true;
    return new Date(value) <= new Date(max);
  },
  message: 'Date must be before or on {max}'
});

extend('url', {
  validate(value) {
    const regex = new RegExp(
      '^(http[s]?:\\/\\/(www\\.)?|ftp:\\/\\/(www\\.)?|(www\\.)?){1}([0-9A-Za-z-\\.@:%_+~#=]+)+((\\.[a-zA-Z]{2,3})+)(/(.)*)?(\\?(.)*)?'
    );
    return regex.test(value);
  },
  message: 'Must be a valid url'
});

extend('valuation_date_not_before', {
  params: ['beforeDate'],

  validate: (value, { beforeDate }) => {
    const inputDate = new Date(value);
    const limitDate = new Date(beforeDate);
    return inputDate >= limitDate;
  },

  message: 'The date should not be earlier than the date of valuation'
});

extend('sm_urls', {
  validate(value) {
    const fb_regex = new RegExp(
      /(https?:)?\/?\/?(www.)?(fb.com|facebook.com)\/[a-zA-Z0-9_]+.*/g
    );
    const twitter_regex = new RegExp(
      /(https?:)?\/?\/?(www.)?(twitter.com)\/[a-zA-Z0-9_]+.*/g
    );
    const x_regex = new RegExp(
      /(https?:)?\/?\/?(www.)?(x.com)\/[a-zA-Z0-9_]+.*/g
    );
    const yt_regex = new RegExp(
      /(https?:)?\/?\/?(www.)?(youtube.com)\/[a-zA-Z0-9_]+.*/g
    );
    const insta_regex = new RegExp(
      /(https?:)?\/?\/?(www.)?(instagram.com)\/[a-zA-Z0-9_]+.*/g
    );
    const linkedin_regex = new RegExp(
      /(https?:)?\/?\/?(www.)?(linkedin.com)\/[a-zA-Z0-9_]+.*/g
    );
    const reddit_regex = new RegExp(
      /(https?:)?\/?\/?(www.)?(reddit.com)\/[a-zA-Z0-9_]+.*/g
    );
    return (
      fb_regex.test(value) ||
      twitter_regex.test(value) ||
      yt_regex.test(value) ||
      insta_regex.test(value) ||
      linkedin_regex.test(value) ||
      reddit_regex.test(value) ||
      x_regex.test(value)
    );
  },
  message: 'Must be a valid social media url'
});

extend('negative_integer', {
  validate: (value) => {
    return value % 1 === 0;
  },
  message: 'Value must be non-decimal'
});

extend('date', {
  validate: (value) => {
    const date = moment(value);
    return date.isValid();
  },
  message: 'Date is invalid'
});

extend('dob', {
  params: ['min'],
  validate(value, { min }) {
    const age = moment().diff(value, 'years', false);

    return Number(age) >= min;
  },
  message: 'Applicant must be at least {min} year(s) old.'
});

extend('time_greater_than', {
  params: ['start'],
  validate(value, { start }) {
    const e = Number((value || '').replace(':', ''));
    const s = Number((start || '').replace(':', ''));
    return e > s;
  },
  message: 'Must be greater than {start}'
});

extend('time_less_than', {
  params: ['end'],
  validate(value, { end }) {
    const e = Number((end || '').replace(':', ''));
    const s = Number((value || '').replace(':', ''));
    return s < e;
  },
  message: 'Must be less than {end}'
});

extend('date_greater_than', {
  params: ['start'],
  validate(value, { start }) {
    const e = new Date(value);
    const s = new Date(start);
    return e > s;
  },
  message: 'Must be greater than {start}'
});

extend('date_less_than', {
  params: ['end'],
  validate(value, { end }) {
    const e = new Date(end);
    const s = new Date(value);
    return s < e;
  },
  message: 'Must be less than {end}'
});

extend('number', {
  validate(value) {
    return !isNaN(value);
  },
  message: 'Must be a numeric value'
});

extend('trn', {
  validate(value) {
    if (!value) return true;
    const regex = /^[A-Za-z0-9]{8,17}$/;
    return regex.test(value);
  },
  message: 'Invalid Tax Number'
});

extend('crontab', {
  validate: (value) => {
    return isValidCron(value);
  },
  message:
    'Invalid crontab expression. See https://crontab.guru for possible configurations'
});

extend('excel_cell', {
  validate: (value) => {
    if (!value) return true;

    const cell = value.match(/([a-zA-Z]+)([0-9]+)/);
    return !!cell?.[0] && !!cell?.[1];
  },
  message:
    'Invalid cell. Ensure cell is in the format {column}{number}. For example: A1'
});

/**
 * @name registerValidationComponent
 * @link plugins/validator.ts
 * @param app `VueConstructor`
 *
 * loads and bootstraps validation form inputs
 */
export function registerValidationComponent(app) {
  app.component('ValidationProvider', ValidationProvider);
  app.component('ValidationObserver', ValidationObserver);
}